// ============================================================
//  src/admin/pages/AdminAdminsPage.js
//  Superadmin-only. Backend enforces this via authSuperAdmin();
//  this page also self-checks so a non-superadmin who lands here
//  directly gets a clear message instead of a raw 403.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAdminAuth } from '../context/AdminAuthContext';
import { listAdmins, createAdmin, setAdminActive, setAdminRole, resetAdminPassword } from '../api/adminClient';
import { PageHeader, Badge, isFlagTrue } from '../components/UI';
import DataTable from '../components/DataTable';
import { useConfirm } from '../components/ConfirmModal';

const EMPTY_FORM = { aName: '', aEmail: '', aPassword: '', aRole: 'admin' };

function fmtDate(d) {
  if (!d) return 'Never';
  const dt = new Date(d.replace(' ', 'T'));
  return isNaN(dt.getTime()) ? 'Never' : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function AdminAdminsPage() {
  const { admin: me } = useAdminAuth();
  const confirm = useConfirm();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listAdmins()
      .then(res => setAdmins(res.data.admins))
      .catch(err => { if (err?.response?.status === 403) setForbidden(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await createAdmin(form);
      toast.success('Admin created');
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not create admin');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(a) {
    const activating = !isFlagTrue(a.aIsActive);
    if (!activating) {
      const ok = await confirm({
        title: `Deactivate ${a.aName}?`,
        message: 'They will be logged out immediately and unable to sign in until reactivated.',
        confirmText: 'Deactivate',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusyId(a.aId);
    try {
      await setAdminActive(a.aId, activating);
      toast.success(activating ? 'Admin activated' : 'Admin deactivated');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleRoleChange(a, newRole) {
    if (newRole === a.aRole) return;
    const ok = await confirm({
      title: newRole === 'superadmin' ? `Grant superadmin to ${a.aName}?` : `Remove superadmin from ${a.aName}?`,
      message: newRole === 'superadmin'
        ? 'They will be able to manage other admin accounts, including deactivating them.'
        : 'They will lose the ability to manage other admin accounts.',
      confirmText: 'Confirm',
      tone: 'danger',
    });
    if (!ok) return;
    setBusyId(a.aId);
    try {
      await setAdminRole(a.aId, newRole);
      toast.success('Role updated');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleResetPassword(a) {
    const ok = await confirm({
      title: `Reset password for ${a.aName}?`,
      message: 'A new temporary password will be generated and their current one will stop working immediately.',
      confirmText: 'Reset Password',
      tone: 'danger',
    });
    if (!ok) return;
    setBusyId(a.aId);
    try {
      const res = await resetAdminPassword(a.aId);
      toast.success(`New temporary password: ${res.data.new_password}`, { duration: 12000, icon: '🔑' });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  }

  if (forbidden) {
    return (
      <div className="bf-page">
        <PageHeader title="Admins" />
        <div className="bf-empty">You need superadmin access to manage admin accounts.</div>
      </div>
    );
  }

  const columns = [
    {
      key: 'name', label: 'Name', primary: true,
      render: a => <>{a.aName} {Number(a.aId) === Number(me?.aId) && <span className="bf-muted" style={{ fontWeight: 500 }}>(you)</span>}</>,
    },
    { key: 'email', label: 'Email', render: a => a.aEmail },
    {
      key: 'role', label: 'Role',
      render: a => (
        <select
          className="bf-select"
          style={{ minWidth: 130, padding: '5px 10px', fontSize: 12.5 }}
          value={a.aRole}
          disabled={busyId === a.aId}
          onChange={e => handleRoleChange(a, e.target.value)}
        >
          <option value="admin">Admin</option>
          <option value="superadmin">Superadmin</option>
        </select>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: a => isFlagTrue(a.aIsActive) ? <Badge tone="success">Active</Badge> : <Badge tone="muted">Deactivated</Badge>,
    },
    { key: 'lastLogin', label: 'Last Login', render: a => fmtDate(a.aLastLoginAt) },
    {
      key: 'actions', label: '', align: 'right',
      render: a => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <button
            className="btn btn-outline bf-btn-sm" style={{ marginRight: 6 }}
            disabled={busyId === a.aId} onClick={() => handleResetPassword(a)}
          >
            Reset Password
          </button>
          {Number(a.aId) === Number(me?.aId) ? (
            <span className="bf-muted" style={{ fontSize: 11.5 }}>—</span>
          ) : isFlagTrue(a.aIsActive) ? (
            <button className="bf-btn-danger-outline bf-btn-sm" disabled={busyId === a.aId} onClick={() => handleToggleActive(a)}>
              Deactivate
            </button>
          ) : (
            <button className="btn btn-primary bf-btn-sm" disabled={busyId === a.aId} onClick={() => handleToggleActive(a)}>
              Activate
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bf-page">
      <PageHeader
        title="Admins"
        subtitle="Manage who can access this panel and what they're allowed to do."
        actions={<button className="btn btn-primary" onClick={() => setShowForm(s => !s)}>+ New Admin</button>}
      />

      {showForm && (
        <div className="bf-card">
          <div className="bf-card-hdr">New Admin</div>
          <form onSubmit={handleCreate} className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="bf-form-grid">
              <div className="bf-field">
                <label className="bf-field-label">Name</label>
                <input className="bf-input" required value={form.aName} onChange={e => setForm({ ...form, aName: e.target.value })} />
              </div>
              <div className="bf-field">
                <label className="bf-field-label">Email</label>
                <input className="bf-input" type="email" required value={form.aEmail} onChange={e => setForm({ ...form, aEmail: e.target.value })} />
              </div>
              <div className="bf-field">
                <label className="bf-field-label">Temporary Password</label>
                <input className="bf-input" type="text" required minLength={8} value={form.aPassword} onChange={e => setForm({ ...form, aPassword: e.target.value })} />
                <div className="bf-field-hint">At least 8 characters. Share this with them securely.</div>
              </div>
              <div className="bf-field">
                <label className="bf-field-label">Role</label>
                <select className="bf-select" value={form.aRole} onChange={e => setForm({ ...form, aRole: e.target.value })}>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
            </div>
            <div className="bf-btn-row">
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create Admin'}</button>
              <button className="btn btn-outline" type="button" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bf-card">
        <div className="bf-card-body-flush">
          <DataTable
            columns={columns}
            rows={admins}
            keyField="aId"
            loading={loading}
            loadingMessage="Loading admins…"
            emptyMessage="No admins found."
          />
        </div>
      </div>
    </div>
  );
}